/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 16/05/22
 */

import Icons from '@/components/icons/Icons';
import { withNamedJsonFormsLayoutProps } from '@/forms-renderers/chakra-ui/utils/withNamedJsonFormsHOC';
import { namedMemo } from '@/utils/namedMemo';
import { IconProps } from '@chakra-ui/react';
import { and, LayoutProps, RankedTester, rankWith, uiTypeIs } from '@jsonforms/core';
import { UISchemaElement } from '@jsonforms/core/src/models/uischema';
import merge from 'lodash/merge';
import React from 'react';

interface IconOptions {
    size?: IconProps['w'];
}

interface IconElement extends UISchemaElement {
    type: 'Icon';
    variant: keyof typeof IconVariants;
}

const IconVariants = {
    strategyConnectedResearch: {
        icon: Icons.ConnectedResearch,
        alt: 'UTS 2027 Strategy - Connected Research Icon',
    },
    strategyDistinctiveIdentity: {
        icon: Icons.DistinctiveIdentity,
        alt: 'UTS 2027 Strategy - Our Distinctive Identity Icon',
    },
    strategyLifetimeOfLearning: {
        icon: Icons.LifetimeOfLearning,
        alt: 'UTS 2027 Strategy - Lifetime of Learning Icon',
    },
    strategyWorkingTogether: {
        icon: Icons.WorkingTogether,
        alt: 'UTS 2027 Strategy - Working Together Icon',
    },
    strategySustainablePartnerships: {
        icon: Icons.SustainablePartnerships,
        alt: 'UTS 2027 Strategy - Sustainable Partnerships Icon',
    },
};

export const ChakraIconRenderer = namedMemo('ChakraIconRenderer', ({ uischema, visible, config }: LayoutProps) => {
    const iconElement = uischema as IconElement;

    const defaultOptions: IconOptions = {
        size: '16',
    };
    const appliedUiSchemaOptions: IconOptions = merge(defaultOptions, config, uischema.options);

    if (visible) {
        const variant = IconVariants[iconElement.variant];
        const Icon = variant.icon;
        return <Icon w={appliedUiSchemaOptions.size} h={appliedUiSchemaOptions.size} aria-label={variant.alt} />;
    } else {
        return null;
    }
});

export const chakraIconRendererTester: RankedTester = rankWith(
    1,
    and(uiTypeIs('Icon'), (uischema) => {
        return 'variant' in uischema && (uischema as IconElement).variant in IconVariants;
    }),
);

export default withNamedJsonFormsLayoutProps(ChakraIconRenderer);
